import { useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { chevronStyles, triggerStyles } from "#components/SelectField";
import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { ChevronDownIcon } from "#icons";
import { cn, customTv } from "#utils";
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  Button as AriaButton,
  ComboBoxStateContext,
  Group,
  Header,
  Input,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  ListBoxSection,
  type SectionProps,
  Popover,
  type Key,
} from "react-aria-components";

import type { PopoverPlacement } from "../shared/MultiDropdownBase";

const popoverStyles = customTv({
  base: "rounded-1 border border-outline-decorative bg-background-secondary shadow-md outline-none flex flex-col",
});

const listBoxStyles = customTv({
  base: "flex max-h-[19.75rem] px-xxs py-xs gap-y-xs flex-col overflow-y-auto overscroll-y-contain outline-none scrollbar-custom",
});

const inputStyles = customTv({
  base: "flex-1 truncate bg-transparent text-left outline-none p-0 w-full",
  variants: {
    withSearch: {
      false: "cursor-pointer",
    },
    isDisabled: {
      true: "text-text-disabled placeholder:text-text-disabled cursor-not-allowed",
      false: "text-text-primary placeholder:text-text-secondary",
    },
  },
});

interface SingleSelectComboBoxInnerProps<T extends object> {
  assistiveText?: ReactNode;
  children: ReactNode | ((item: T) => ReactNode) | undefined;
  emptyMessage: string;
  isDisabled: boolean;
  isInvalid: boolean;
  isOpen: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string;
  placeholder: string;
  placement: PopoverPlacement;
  tooltipText?: ReactNode;
  triggerClassName?: string;
  withSearch: boolean;
}

function SingleSelectComboBoxInner<T extends object>({
  assistiveText,
  children,
  emptyMessage,
  isDisabled,
  isInvalid,
  isOpen,
  isReadOnly,
  isRequired,
  label,
  placeholder,
  placement,
  tooltipText,
  triggerClassName,
  withSearch,
}: SingleSelectComboBoxInnerProps<T>) {
  const state = useContext(ComboBoxStateContext);
  const groupRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    const el = groupRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      setTriggerWidth(entries[0].target.clientWidth);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <Group
        className={triggerStyles({
          isDisabled,
          isInvalid,
          isOpen,
          isReadOnly,
          className: cn(
            triggerClassName,
            isReadOnly
              ? "focus-within:ring-0"
              : "focus-within:!border-outline-input-focus focus-within:ring-1 focus-within:!ring-outline-input-focus",
          ),
        })}
        ref={groupRef}
      >
        <Input
          className={inputStyles({ isDisabled, withSearch })}
          onClick={() => {
            // `menuTrigger="focus"` only fires when the input transitions from unfocused → focused.
            // Once a value is selected the input stays focused, so clicking it again won't reopen
            // the popover. ComboBoxStateContext (available inside AriaComboBox's tree) is read here
            // to explicitly reopen the popover on click when it is already closed.
            if (state && !state.isOpen) {
              state.open(null, "manual");
            }
          }}
          placeholder={placeholder}
          readOnly={!withSearch || isReadOnly}
        />
        <AriaButton className="flex shrink-0 items-center pl-lg outline-none">
          <ChevronDownIcon
            aria-hidden="true"
            className={chevronStyles({
              isOpen,
              isDisabled,
              className: "ml-0",
            })}
          />
        </AriaButton>
      </Group>
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
      <Popover
        className={popoverStyles()}
        offset={8}
        placement={placement}
        style={{ width: `${triggerWidth}px` }}
        triggerRef={groupRef}
      >
        <ListBox
          className={listBoxStyles()}
          renderEmptyState={() => (
            <div className="px-sm text-label-medium text-text-secondary">{emptyMessage}</div>
          )}
        >
          {children}
        </ListBox>
      </Popover>
    </>
  );
}

export interface SingleSelectComboBoxProps<T extends object> extends Omit<
  AriaComboBoxProps<T>,
  "children" | "className" | "selectionMode"
> {
  /** Label text displayed above the field */
  label?: string;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Tooltip text shown next to the label */
  tooltipText?: ReactNode;
  /** Assistive text displayed below the field */
  assistiveText?: ReactNode;
  /** Additional class name for the outer wrapper */
  className?: string;
  /** Empty state message when no options match */
  emptyMessage?: string;
  /** Popover placement relative to the trigger */
  placement?: PopoverPlacement;
  /** Additional class name for the trigger */
  triggerClassName?: string;
  /** Enable search/filtering in the trigger input. When false, clicking opens the popover without typing. */
  withSearch?: boolean;
  /** Collection items (static ComboBoxItem/ComboBoxSection elements or render function) */
  children: ReactNode | ((item: T) => ReactNode);
}

/**
 * SingleSelectComboBox - A combo box that combines a searchable text input with a listbox.
 *
 * Built on React Aria's ComboBox primitive. The trigger field IS the search input,
 * so filtering happens inline as you type. Supports declarative items and sections.
 *
 * @example Static items
 * ```tsx
 * <SingleSelectComboBox label="Provider" placeholder="Select a provider...">
 *   <ComboBoxItem id="aws">AWS</ComboBoxItem>
 *   <ComboBoxItem id="gcp">GCP</ComboBoxItem>
 * </SingleSelectComboBox>
 * ```
 *
 * @example With sections
 * ```tsx
 * <SingleSelectComboBox label="Region" placeholder="Select region...">
 *   <ComboBoxSection title="North America">
 *     <ComboBoxItem id="us-east-1">US East</ComboBoxItem>
 *   </ComboBoxSection>
 *   <ComboBoxSection title="Europe">
 *     <ComboBoxItem id="eu-west-1">Ireland</ComboBoxItem>
 *   </ComboBoxSection>
 * </SingleSelectComboBox>
 * ```
 *
 * @example Dynamic collection
 * ```tsx
 * <SingleSelectComboBox label="Region" defaultItems={regions}>
 *   {(item) => <ComboBoxItem id={item.id}>{item.name}</ComboBoxItem>}
 * </SingleSelectComboBox>
 * ```
 *
 * @example Controlled
 * ```tsx
 * <SingleSelectComboBox
 *   label="Region"
 *   selectedKey={selectedKey}
 *   onSelectionChange={setSelectedKey}
 *   inputValue={searchText}
 *   onInputChange={setSearchText}
 *   items={filteredRegions}
 * >
 *   {(item) => <ComboBoxItem id={item.id}>{item.name}</ComboBoxItem>}
 * </SingleSelectComboBox>
 * ```
 */
export function SingleSelectComboBox<T extends object>({
  label,
  placeholder = "Select an option...",
  tooltipText,
  assistiveText,
  isInvalid = false,
  className,
  emptyMessage = "No options found...",
  placement = "bottom",
  triggerClassName,
  withSearch = true,
  children,
  ...comboBoxProps
}: SingleSelectComboBoxProps<T>) {
  return (
    <AriaComboBox
      {...comboBoxProps}
      aria-label={comboBoxProps["aria-label"] ?? label ?? "combobox"}
      className={cn("flex flex-col gap-[.15rem]", className)}
      isInvalid={isInvalid}
      menuTrigger="focus"
      allowsEmptyCollection
    >
      {({ isOpen, isDisabled }) => (
        <SingleSelectComboBoxInner
          assistiveText={assistiveText}
          emptyMessage={emptyMessage}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isOpen={isOpen}
          isReadOnly={comboBoxProps.isReadOnly}
          isRequired={comboBoxProps.isRequired}
          label={label}
          placeholder={placeholder}
          placement={placement}
          tooltipText={tooltipText}
          triggerClassName={triggerClassName}
          withSearch={withSearch}
        >
          {children}
        </SingleSelectComboBoxInner>
      )}
    </AriaComboBox>
  );
}

/** A single option item within a SingleSelectComboBox. Wraps RAC ListBoxItem with project styling. */

const itemStyles = customTv({
  base: "flex cursor-default items-center rounded-sm px-xs py-3xs text-label-medium text-text-primary outline-none hover:cursor-pointer",
  variants: {
    isFocused: {
      // text-text-primary (the base class) already adapts per theme, matching
      // the equally theme-aware highlight background -- unlike a hardcoded
      // ink color, which reads fine in light mode (light highlight) but goes
      // illegible in dark mode, where the highlight itself turns dark.
      true: "bg-interactive-secondary-button-hover-background",
    },
    isSelected: {
      true: "bg-interactive-secondary-button-focus-background",
    },
  },
});

export function ComboBoxItem(props: ListBoxItemProps) {
  return (
    <ListBoxItem
      {...props}
      className={({ isFocused, isSelected }) =>
        itemStyles({
          isFocused,
          isSelected,
          className: typeof props.className === "string" ? props.className : undefined,
        })
      }
    />
  );
}

/** A grouped section within a SingleSelectComboBox. Wraps RAC ListBoxSection with a styled header. */

const sectionHeaderStyles = customTv({
  base: "pointer-events-none select-none border-b border-outline-decorative p-3xs text-title-medium",
});

export interface ComboBoxSectionProps<T extends object> extends SectionProps<T> {
  /** Section header text */
  title?: string;
}

export function ComboBoxSection<T extends object>({
  title,
  children,
  ...rest
}: ComboBoxSectionProps<T>) {
  return (
    <ListBoxSection {...rest} className="flex flex-col gap-y-xxs px-xs">
      {title ? <Header className={sectionHeaderStyles()}>{title}</Header> : null}
      {children as ReactNode}
    </ListBoxSection>
  );
}

export type { Key as ComboBoxKey };
